/**
 * Per-drop group selector – assign a prompt group to each drop.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Layers } from 'lucide-react';

import { cn } from '@/lib/utils';

import {
  getActiveSelectableLeafGroups,
  getActiveSelectableLeafGroupsUnderParent,
  getRandomizeScopeFolderOptions,
  optionLabelForGroup,
} from '../utils/group-tree.utils';

import { PerDropGroupSelectorDropRows } from './PerDropGroupSelectorDropRows';
import { PerDropGroupSelectorRandomizeToolbar } from './PerDropGroupSelectorRandomizeToolbar';
import type { PerDropGroupSelectorProps } from './PerDropGroupSelector.types';

export type { DropLabel } from './PerDropGroupSelector.types';

const MASTER_ID = 'master';

function uniqueIds(ids: string[]): string[] {
  return Array.from(new Set(ids.filter(Boolean)));
}

export const PerDropGroupSelector: FC<PerDropGroupSelectorProps> = ({
  dropCount,
  groupIdsPerDrop,
  defaultGroupId,
  groups,
  flatList = false,
  onChange,
  dropLabels,
  className,
}) => {
  const [randomizeScopeIds, setRandomizeScopeIds] = useState<string[]>([]);

  const leaves = useMemo(() => {
    if (flatList) return groups.filter((g) => g.is_active);
    return getActiveSelectableLeafGroups(groups);
  }, [groups, flatList]);

  const folderScopeOptions = useMemo(
    () => (flatList ? [] : getRandomizeScopeFolderOptions(groups)),
    [groups, flatList],
  );

  useEffect(() => {
    const validIds = new Set(folderScopeOptions.map((f) => f.id));
    setRandomizeScopeIds((prev) => {
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [folderScopeOptions]);

  const options = useMemo(
    () => [
      { id: MASTER_ID, name: 'Default' },
      ...leaves.map((g) => ({
        id: g.id,
        name: flatList ? g.name : optionLabelForGroup(g, groups),
      })),
    ],
    [groups, leaves, flatList],
  );

  const randomizePoolIds = useMemo(() => {
    if (flatList || randomizeScopeIds.length === 0) {
      return options.map((g) => g.id);
    }
    return uniqueIds(
      randomizeScopeIds.flatMap((scopeId) =>
        getActiveSelectableLeafGroupsUnderParent(groups, scopeId).map((g) => g.id),
      ),
    );
  }, [flatList, randomizeScopeIds, options, groups]);

  const setGroupForDrop = (dropIdx: number, groupId: string) => {
    const next = [...groupIdsPerDrop];
    next[dropIdx] = groupId;
    onChange(next);
  };

  const randomize = () => {
    const ids = randomizePoolIds;
    if (ids.length === 0) return;
    onChange(
      Array.from({ length: dropCount }, () => ids[Math.floor(Math.random() * ids.length)]!),
    );
  };

  const canRandomize =
    dropCount > 0 &&
    randomizePoolIds.length >= 1 &&
    (randomizeScopeIds.length > 0 || options.length > 1);

  const showScopeSelect = !flatList && folderScopeOptions.length > 0;
  const randomizeButtonLabel =
    randomizeScopeIds.length === 0
      ? 'Randomize all'
      : randomizeScopeIds.length === 1
        ? 'Randomize in folder'
        : 'Randomize in folders';

  const setFolderScope = (folderId: string, checked: boolean) => {
    setRandomizeScopeIds((prev) =>
      checked ? uniqueIds([...prev, folderId]) : prev.filter((id) => id !== folderId),
    );
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <Layers className="h-3 w-3" />
          Group per drop
        </label>
        <PerDropGroupSelectorRandomizeToolbar
          randomizeScopeIds={randomizeScopeIds}
          onClearRandomizeScopes={() => setRandomizeScopeIds([])}
          onFolderScopeChange={setFolderScope}
          folderScopeOptions={folderScopeOptions}
          showScopeSelect={showScopeSelect}
          randomizeButtonLabel={randomizeButtonLabel}
          canRandomize={canRandomize}
          onRandomize={randomize}
        />
      </div>
      <PerDropGroupSelectorDropRows
        dropCount={dropCount}
        dropLabels={dropLabels}
        groupIdsPerDrop={groupIdsPerDrop}
        defaultGroupId={defaultGroupId}
        options={options}
        onGroupChange={setGroupForDrop}
      />
    </div>
  );
};
